import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { EnrollmentCode } from '../components/EnrollmentCode';
import { Button, Card, Dialog, Empty, ErrorNote, Field, inputClass, Loading } from '../components/ui';
import { api, unwrap, type Device, type Enrollment } from '../lib/api';
import { fmtAgo, fmtTime } from '../lib/format';
import { useDevices } from '../lib/queries';
import { usePlantContext } from './Plant';

const STATUS: Record<string, string> = { pending: 'Waiting for enrollment', active: 'Active', revoked: 'Revoked' };

export function PlantDevices() {
  const plant = usePlantContext();
  const devices = useDevices(plant.slug);
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [shown, setShown] = useState<Enrollment | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['devices', plant.slug] });

  const reissue = useMutation({
    mutationFn: (d: Device) => unwrap(api.POST('/devices/{deviceId}/enrollment-code', { params: { path: { deviceId: d.id } } })),
    onSuccess: (r) => { setShown(r.enrollment); refresh(); },
  });
  const revoke = useMutation({
    mutationFn: (d: Device) => unwrap(api.POST('/devices/{deviceId}/revoke', { params: { path: { deviceId: d.id } } })),
    onSuccess: refresh,
  });

  if (devices.isPending) return <Loading />;
  if (devices.error) return <ErrorNote error={devices.error} />;

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-muted">The edge computer (IPC) that sends this plant's data with vardex-sync.</p>
        <Button variant="primary" onClick={() => setAdding(true)}>Add device</Button>
      </div>
      <ErrorNote error={reissue.error ?? revoke.error} />
      {devices.data.length === 0 ? (
        <Empty>No devices yet. Add one to connect the plant's IPC.</Empty>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Device</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Last seen</th>
                <th className="px-3 py-2 font-medium">Version</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {devices.data.map((d) => (
                <tr key={d.id} className="border-t border-rule">
                  <td className="px-3 py-2">
                    <div className="font-medium">{d.name}</div>
                    <div className="text-xs text-muted">{d.hostname ?? d.id.slice(0, 8)}</div>
                  </td>
                  <td className="px-3 py-2">{STATUS[d.status] ?? d.status}</td>
                  <td className="px-3 py-2" title={d.lastSeenAt ? fmtTime(d.lastSeenAt, plant.timezone, { seconds: true }) : undefined}>
                    {d.lastSeenAt ? fmtAgo(d.lastSeenAt) : '–'}
                  </td>
                  <td className="tabular px-3 py-2">{d.syncVersion ?? '–'}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {d.status !== 'revoked' && (
                      <>
                        <Button variant="ghost" onClick={() => reissue.mutate(d)} disabled={reissue.isPending}>New code</Button>
                        <Button
                          variant="ghost"
                          className="text-critical"
                          onClick={() => { if (confirm(`Revoke ${d.name}? It can no longer send data. Its data stays.`)) revoke.mutate(d); }}
                        >
                          Revoke
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={adding} onClose={() => setAdding(false)} title="Add device">
        <AddDevice plant={String(plant.id)} timezone={plant.timezone} onDone={refresh} />
      </Dialog>
      <Dialog open={!!shown} onClose={() => setShown(null)} title="New enrollment code">
        {shown && <EnrollmentCode enrollment={shown} timezone={plant.timezone} />}
      </Dialog>
    </>
  );
}

/** Name the device, then show the code and commands. Also used on the Admin page. */
export function AddDevice({ plant, timezone, onDone }: { plant: string; timezone: string; onDone: () => void }) {
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => unwrap(api.POST('/plants/{plant}/devices', { params: { path: { plant } }, body: { name, validHours: 24 } })),
    onSuccess: onDone,
  });
  if (create.data) return <EnrollmentCode enrollment={create.data.enrollment} timezone={timezone} />;
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
      <Field label="Name" hint="Which computer this is, e.g. the IPC's serial number.">
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="ERKO-STO-01 IPC" required autoFocus />
      </Field>
      <ErrorNote error={create.error} />
      <Button type="submit" variant="primary" className="mt-2" disabled={create.isPending}>Create and show code</Button>
    </form>
  );
}
